/**
 * Olo newsletter forms + popups
 *
 * Forms are plain Shopify customer forms (snippets/olo-newsletter-form.liquid).
 * Signups are saved as Shopify customers tagged `olo_pending`; a Shopify Flow
 * sends them to Olo Engage. No Olo API key lives in the theme.
 *
 * The Olo API doesn't accept browser calls (CORS), so this script never
 * contacts Olo itself. It:
 *   - validates the form, then lets it post normally, so Shopify's hCaptcha
 *     can attach its token (a background fetch can't, and Shopify rejects it)
 *   - records the signup after Shopify redirects back with ?customer_posted=true
 *   - opens popups (sections/olo-popups.liquid) based on their trigger settings;
 *     popups marked "first-time visitors only" open only during the visit that
 *     set the olo_visitor cookie
 */

(function () {
	const STORAGE_PREFIX = 'olo_popup_';
	const SUBSCRIBED_KEY = 'olo_subscribed';
	const SUBSCRIBED_DAYS = 365;
	const DEBUG_KEY = 'olo_debug';
	const VISITOR_COOKIE = 'olo_visitor';
	const VISITOR_DAYS = 365;
	const FIRST_VISIT_KEY = 'olo_first_visit';

	/* ---------- Debug logging ---------- */

	// Visit any page with ?olo_debug=1 to turn logging on for this browser, ?olo_debug=0 to turn it off.
	// Stored in localStorage so it survives the page reload after a form post.
	const DEBUG = (function () {
		try {
			const param = new URLSearchParams(window.location.search).get(DEBUG_KEY);
			if (param === '1') localStorage.setItem(DEBUG_KEY, '1');
			if (param === '0') localStorage.removeItem(DEBUG_KEY);
			return localStorage.getItem(DEBUG_KEY) === '1';
		} catch (_) {
			return false;
		}
	})();

	function debug(...args) {
		if (DEBUG) console.log('%c[Olo Forms]', 'color:#0a7cff;font-weight:bold', ...args);
	}

	/* ---------- Storage helpers (localStorage can throw in private mode) ---------- */

	function setFlag(key, days) {
		try {
			localStorage.setItem(key, String(Date.now() + days * 864e5));
		} catch (_) {}
	}

	function hasFlag(key) {
		try {
			const expires = Number(localStorage.getItem(key));
			return expires > Date.now();
		} catch (_) {
			return false;
		}
	}

	function getCookie(name) {
		return document.cookie.split('; ').some((pair) => pair.split('=')[0] === name);
	}

	function setCookie(name, days) {
		document.cookie = name + '=1; max-age=' + days * 86400 + '; path=/; SameSite=Lax';
	}

	// A visitor without the olo_visitor cookie is new. The cookie is set right away,
	// so the session flag keeps the rest of this first visit (later page views) "first".
	function detectFirstVisit() {
		try {
			if (sessionStorage.getItem(FIRST_VISIT_KEY)) return true;
			if (getCookie(VISITOR_COOKIE)) return false;
			setCookie(VISITOR_COOKIE, VISITOR_DAYS);
			sessionStorage.setItem(FIRST_VISIT_KEY, '1');
			return true;
		} catch (_) {
			return false;
		}
	}

	/* ---------- Forms ---------- */

	function showMessage(form, text, type) {
		const message = form.querySelector('[data-olo-form-message]');
		if (!message) return;
		message.classList.remove('olo-form-message--error', 'olo-form-message--success');
		if (type) message.classList.add('olo-form-message--' + type);
		message.textContent = text;
	}

	// requestSubmit fires a real submit event, which Shopify's hCaptcha listens
	// for to add its token. form.submit() skips the event and the post is rejected.
	function nativeSubmit(form) {
		form.dataset.oloNative = 'true';
		if (typeof form.requestSubmit === 'function') {
			form.requestSubmit();
		} else {
			HTMLFormElement.prototype.submit.call(form);
		}
	}

	function onSuccess(form) {
		setFlag(SUBSCRIBED_KEY, SUBSCRIBED_DAYS);
		form.classList.add('olo-newsletter-form--success');

		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({ event: 'newsletter_signup', form_id: form.id });
		form.dispatchEvent(new CustomEvent('olo:subscribe', { bubbles: true }));
	}

	// Shopify redirects a successful post to ?customer_posted=true#<form id>
	// and renders the success message server-side; this records the signup.
	function handlePostedForm() {
		if (!window.location.search.includes('customer_posted=true') || !window.location.hash) return;
		const form = document.getElementById(window.location.hash.slice(1));
		if (!form || !form.matches('form[data-olo-form]')) return;

		debug('Shopify signup succeeded for', form.id);
		onSuccess(form);
	}

	// Returns the first invalid required field, marking each field's aria-invalid state
	function validate(form) {
		let firstInvalid = null;
		form.querySelectorAll('input[required]').forEach((input) => {
			input.value = input.value.trim();
			if (input.checkValidity()) {
				input.removeAttribute('aria-invalid');
			} else {
				input.setAttribute('aria-invalid', 'true');
				firstInvalid = firstInvalid || input;
			}
		});
		return firstInvalid;
	}

	function invalidMessage(input) {
		if (input.type === 'email') return 'Please enter a valid email address.';
		const label = input.labels && input.labels[0];
		return label ? 'Please enter your ' + label.textContent.trim().toLowerCase() + '.' : 'Please fill in all fields.';
	}

	function handleSubmit(event) {
		const form = event.target;
		if (form.dataset.oloNative) return;

		event.preventDefault();

		const invalid = validate(form);
		if (invalid) {
			showMessage(form, invalidMessage(invalid), 'error');
			invalid.focus();
			return;
		}

		showMessage(form, 'Submitting…');
		debug('Posting to Shopify (page will reload):', { action: form.action, fields: Object.fromEntries(new FormData(form)) });
		nativeSubmit(form);
	}

	function initForms() {
		document.querySelectorAll('form[data-olo-form]').forEach((form) => {
			form.addEventListener('submit', handleSubmit);
		});
	}

	/* ---------- Popups ---------- */

	function dismissKey(popup) {
		return STORAGE_PREFIX + popup.dataset.oloPopup;
	}

	function isSuppressed(popup, firstVisit) {
		if (popup.dataset.firstVisit === 'true' && !firstVisit) return true;
		return hasFlag(SUBSCRIBED_KEY) || hasFlag(dismissKey(popup));
	}

	function openPopup(popup) {
		if (popup.open || document.querySelector('dialog.olo-popup[open]')) return;
		debug('Opening popup', popup.dataset.oloPopup);
		popup.showModal();
		// Count a first-visit popup as seen once shown, so it doesn't reopen on the next page
		if (popup.dataset.firstVisit === 'true') {
			setFlag(dismissKey(popup), Number(popup.dataset.dismissDays) || 14);
		}
	}

	function bindPopup(popup) {
		popup.querySelector('[data-olo-popup-close]')?.addEventListener('click', () => popup.close());

		// Clicking the backdrop (the dialog element itself, outside the content) closes it
		popup.addEventListener('click', (event) => {
			if (event.target === popup) popup.close();
		});

		popup.addEventListener('close', () => {
			if (!hasFlag(SUBSCRIBED_KEY)) {
				setFlag(dismissKey(popup), Number(popup.dataset.dismissDays) || 14);
			}
		});
	}

	function armTrigger(popup) {
		const trigger = popup.dataset.trigger;

		if (trigger === 'exit_intent') {
			// Mouse leaving through the top of the window; not reliable on touch devices
			if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
			const onLeave = (event) => {
				if (event.clientY > 0 || event.relatedTarget) return;
				document.removeEventListener('mouseout', onLeave);
				openPopup(popup);
			};
			document.addEventListener('mouseout', onLeave);
			return;
		}

		if (trigger === 'scroll') {
			const target = (Number(popup.dataset.scrollPercent) || 50) / 100;
			const onScroll = () => {
				const scrollable = document.documentElement.scrollHeight - window.innerHeight;
				if (scrollable <= 0 || window.scrollY / scrollable < target) return;
				window.removeEventListener('scroll', onScroll);
				openPopup(popup);
			};
			window.addEventListener('scroll', onScroll, { passive: true });
			return;
		}

		setTimeout(() => openPopup(popup), (Number(popup.dataset.delay) || 5) * 1000);
	}

	// After a no-JS post from a popup form, Shopify reloads the page with the
	// form id in the hash. Reopen that popup so the visitor sees the result.
	function reopenPostedPopup(popups) {
		if (!window.location.hash) return false;
		const form = document.getElementById(window.location.hash.slice(1));
		const popup = form && form.closest('dialog.olo-popup');
		if (!popup || !popups.includes(popup)) return false;

		if (window.location.search.includes('customer_posted=true')) {
			setFlag(SUBSCRIBED_KEY, SUBSCRIBED_DAYS);
		}
		popup.showModal();
		return true;
	}

	function initPopups(firstVisit) {
		const popups = Array.from(document.querySelectorAll('dialog.olo-popup'));
		if (!popups.length || typeof HTMLDialogElement !== 'function') return;

		popups.forEach(bindPopup);

		// In the theme editor, show a popup while its block is selected instead of using triggers
		if (window.Shopify && window.Shopify.designMode) {
			document.addEventListener('shopify:block:select', (event) => {
				const popup = event.target.closest('dialog.olo-popup') || event.target.querySelector?.('dialog.olo-popup');
				if (popup && !popup.open) popup.showModal();
			});
			document.addEventListener('shopify:block:deselect', (event) => {
				const popup = event.target.closest('dialog.olo-popup') || event.target.querySelector?.('dialog.olo-popup');
				if (popup && popup.open) popup.close();
			});
			return;
		}

		if (reopenPostedPopup(popups)) return;

		// Arm every popup this visitor qualifies for; openPopup never stacks two at once
		const armed = popups.filter((p) => !isSuppressed(p, firstVisit));
		debug('First visit:', firstVisit, '| Popups armed:', armed.map((p) => p.dataset.oloPopup + ' (' + p.dataset.trigger + ')'));
		armed.forEach(armTrigger);
	}

	function init() {
		debug('Debug logging on. Forms found:', document.querySelectorAll('form[data-olo-form]').length);
		initForms();
		handlePostedForm();
		// Skipped in the theme editor so previewing doesn't mark the merchant as a returning visitor
		const firstVisit = window.Shopify && window.Shopify.designMode ? false : detectFirstVisit();
		initPopups(firstVisit);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
